import { useEffect, type RefObject } from "react";

/**
 * Keeps Tab focus inside <paramref name="containerRef"/> while the popover /
 * dialog is open. Wraps Tab around (last → first), Shift+Tab around the
 * other way, and restores focus to the trigger when the trap detaches.
 *
 * Used by Mail / Connect popovers (LabelPicker, SnoozeMenu, TemplatePicker,
 * NotificationsBell) so keyboard users can't escape the menu mid-action.
 */
export function useFocusTrap(
    containerRef: RefObject<HTMLElement | null>,
    enabled: boolean
) {
    useEffect(() => {
        if (!enabled) return;
        const container = containerRef.current;
        if (!container) return;

        const previouslyFocused = document.activeElement as HTMLElement | null;

        // Move focus inside on first tick so Tab from the trigger lands on
        // the first interactive child, not somewhere on the page.
        const focusables = collectFocusable(container);
        focusables[0]?.focus();

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== "Tab") return;
            const items = collectFocusable(container);
            if (items.length === 0) return;
            const first = items[0]!;
            const last = items[items.length - 1]!;
            const active = document.activeElement as HTMLElement | null;
            if (e.shiftKey) {
                if (active === first || !container.contains(active)) {
                    e.preventDefault();
                    last.focus();
                }
            } else if (active === last) {
                e.preventDefault();
                first.focus();
            }
        };

        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("keydown", onKeyDown);
            previouslyFocused?.focus?.();
        };
    }, [containerRef, enabled]);
}

const FOCUSABLE_SELECTOR = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled]):not([type='hidden'])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[tabindex]:not([tabindex='-1'])"
].join(",");

function collectFocusable(root: HTMLElement): HTMLElement[] {
    return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
        .filter((el) => !el.hasAttribute("disabled") && el.tabIndex !== -1);
}
